/**
 * Web worker exported as a string instead of a separate .ts/.js file.
 *
 * This approach was chosen because web workers have complex module system issues:
 * - TypeScript's --isolatedModules flag requires import/export statements
 * - Web workers can't use ES6 module syntax (import/export) at runtime
 * - Adding export {} satisfies TypeScript but breaks the worker with "Unexpected token 'export'"
 * - Using // @ts-nocheck works but bypasses all type checking
 * - Converting to .js fails CI/CD linting rules
 *
 * By exporting the worker as a string:
 * - No module system conflicts (it's just a string export)
 * - TypeScript compilation works normally
 * - Worker is created via Blob + URL.createObjectURL at runtime
 * - All type checking and linting rules are satisfied
 * - No separate file management or build complications
 */

export const bundleGroupingWorkerScript = `
/// <reference lib="webworker" />

/**
 * Web worker for processing FHIR bundles to group resources by type.
 */
self.addEventListener("message", (event) => {
  const type = "GROUP_BUNDLE";
  const requestId = "auto-generated";
  const bundle = event.data;


  if (!bundle || !bundle.entry) {
    const response = { type, error: "Invalid bundle", requestId };
    return self.postMessage(response);
  }

  try {
    const startTime = performance.now();
    const mappedResources = groupBundleByResourceType(bundle);
    const processingTime = performance.now() - startTime;
    const response = { type, mappedResources, requestId, processingTime };
    self.postMessage(response);
  } catch (error) {
    const response = {
      type,
      error: error instanceof Error ? error.message : "Unknown error",
      requestId,
    };
    self.postMessage(response);
  }
});

function groupBundleByResourceType(bundle) {
  const mappedResources = {};

  bundle.entry?.forEach((entry) => {
    const resource = entry.resource;
    if (resource) {
      if (!mappedResources[resource.resourceType]) {
        mappedResources[resource.resourceType] = {};
      }

      const mappedResource = mappedResources[resource.resourceType];

      if (mappedResource && resource.id) {
        mappedResource[resource.id] = resource;
      }
    }
  });

  return mappedResources;
}
`;
